import { FC } from 'react'
import { Box, Typography } from '@mui/material'
import { Component } from '../../types/ContentStructure'
import ComponentType from './ComponentType'
import ComponentLink from './ComponentLink'

interface IComponentBannerEmphasisProps {
    component: Component
}

/**
 * BANNER ENFASIS (componentTypeId=41)
 *
 * Hero full-width con imagen de fondo, overlay oscuro y texto con énfasis de color.
 * El color de énfasis se controla íntegramente desde CMS (catTheme.backgroundColor).
 *
 * Layout:
 *   Desktop — contenido alineado a la izquierda, imagen a la derecha (background cover)
 *   Mobile  — imagen de fondo a tamaño completo, overlay vertical
 *
 * Campos del componente padre (catComponent):
 *   image / imageMd / imageXs → imagen de fondo por breakpoint
 *   title    → texto introductorio en blanco  (ej. "Hacer y")
 *   subTitle → texto con énfasis de color      (ej. "ver crecer")
 *   text     → cuerpo de apoyo                (ej. "Con una tasa hasta del 14%*\nInvierte desde $1,000.00")
 *   theme    → provee el color de énfasis vía theme.themeStyles.backgroundColor
 *
 * Hijos en components[]:
 *   TEXTO → título grande en blanco           (ej. "¡Tu inversión!")
 *   LINK  → CTA botón                         (ej. "Simula tu inversión")
 *   Cualquier otro tipo soportado por ComponentType.
 *
 * REGLA: nunca hardcodear el color de énfasis aquí.
 * Si el theme no tiene backgroundColor, se cae a navy #001933.
 */
const ComponentBannerEmphasis: FC<IComponentBannerEmphasisProps> = ({ component }) => {
    const { theme, title, subTitle, text, image, imageMd, imageXs, components } = component

    // Color de énfasis viene del catTheme asignado en CMS Admin
    const emphasisColor = theme?.themeStyles?.backgroundColor || '#001933'

    // Imagen de fondo por breakpoint (fallback a image si no hay versión específica)
    const bgXs = imageXs || image || ''
    const bgMd = imageMd || image || ''
    const bgLg = image || ''

    return (
        <Box sx={[{
            width: '100%', color: 'white',
            px: { xs: 3, md: 8 },
        }
        ]}>
            <Box sx={{
                position: 'relative',
                width: '100%',
                //minHeight: { xs: '70vh', sm: '65vh', md: '60vh', lg: '70vh' },
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                borderRadius: { xs: 8, md: 8, lg: 0 }
            }}>
                {/* ── Imagen de fondo por breakpoint ── */}
                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: {
                        xs: `url(${bgXs})`,
                        sm: `url(${bgMd})`,
                        md: `url(${bgLg})`,
                    },
                    backgroundSize: 'cover',
                    backgroundPosition: { xs: 'center', md: 'right center' },
                    zIndex: 0,
                }} />

                {/* ── Overlay: gradiente izquierda → transparente ──
                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    background: {
                        xs: 'linear-gradient(to bottom, rgba(0,18,48,0.92) 0%, rgba(0,18,48,0.75) 100%)',
                        md: 'linear-gradient(to right, rgba(0,18,48,0.95) 0%, rgba(0,18,48,0.80) 50%, rgba(0,18,48,0.20) 100%)',
                    },
                    zIndex: 1,
                }} /> */}

                {/* ── Contenido ── */}
                <Box sx={{
                    position: 'relative',
                    zIndex: 2,
                    width: '100%',
                    px: { xs: 4, sm: 6, md: 8, lg: 12 },
                    py: { xs: 6, md: 8 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'center', md: 'center', lg: 'flex-start' },
                    //gap: { xs: 2, md: 3 },
                }}>

                    {/* Línea superior: title (blanco) + subTitle (color énfasis) en la misma línea */}
                    {(title || subTitle) && (
                        <Typography variant="h2" sx={{
                            color: 'white',
                            fontWeight: 300,
                            m: 0,
                            display: 'inline',
                        }}>
                            {title && <span>{title}</span>}
                            {subTitle && (
                                <span style={{
                                    color: emphasisColor,
                                    //marginTop: '0.1em'
                                    paddingLeft: '0.3em',
                                }}>
                                    {subTitle}
                                </span>
                            )}
                        </Typography>
                    )}

                    {/* Hijos: TEXTO grande (título énfasis) - sin CTA todavía */}
                    {components && components.length > 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'center', md: 'center', lg: 'flex-start' },
                            gap: { xs: 2, md: 2.5 },
                            width: '100%',
                        }}>
                            {components.filter(child => child.componentType.name === 'TEXTO').map((child, idx) => (
                                <Box key={idx} sx={{ width: '100%' }}>
                                    <ComponentType component={child} sx={{
                                        fontSize: 'clamp(2.25rem, 1.446rem + 2.991vw, 4rem)',
                                        textAlign: { xs: 'center', md: 'center', lg: 'left' },
                                    }} />
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Texto de apoyo (cuerpo, datos, disclaimers cortos) */}
                    {text && (
                        <Typography variant="body1" sx={{
                            color: 'rgba(255,255,255,0.95)',
                            whiteSpace: 'pre-line',
                            lineHeight: 1.7,
                            fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                            alignItems: { xs: 'center', md: 'center', lg: 'flex-start' },
                            textAlign: { xs: 'center', md: 'center', lg: 'justify' },
                            fontWeight: 'bold',
                            mt: 1,
                        }}>
                            {text}
                        </Typography>
                    )}

                    {/* CTA (LINK) - va al final */}
                    {components && components.length > 0 && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'center', md: 'center', lg: 'flex-start' },
                            gap: { xs: 2, md: 2.5 },
                            width: '100%',
                            mt: 2,
                        }}>
                            {components.filter(child => child.componentType.name === 'LINK').map((child, idx) => (

                                <Box key={idx} sx={{ py: 2 }}>
                                    <ComponentLink component={child} variant='cta' />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default ComponentBannerEmphasis
